import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CalendarDays, Clock, MapPin, Home as HomeIcon, Globe, UserPlus, KeyRound, CheckCircle2, Hourglass, ShieldCheck, AlertCircle, Pencil } from "lucide-react";
import { pApi, api, errMsg, fmtDate, fmtClock, PART_KEY } from "../lib/api";
import { useLang, LangToggle } from "../lib/i18n";
import { ParishMark, Loading, Field, inputCls, StatusBadge } from "../components/common";
import { ParticipantFields, RepresentFields, Section, emptyParticipant, validParticipant, withAffiliation } from "../components/ParticipantForm";
import { IdCardBlock } from "../components/IdCard";

const Choice = ({ icon: Icon, title, sub, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="w-full text-left sfap-card p-5 sm:p-6 flex items-start gap-4 hover:border-[#2D3B2D] hover:bg-[#FBF8F2] transition-colors min-h-[88px]">
    <span className="h-12 w-12 shrink-0 rounded-2xl bg-[#F5F0E6] grid place-items-center"><Icon className="h-6 w-6 text-[#C85A32]" /></span>
    <span><span className="block font-serif text-lg font-bold text-stone-900">{title}</span><span className="block text-sm text-stone-600 mt-0.5">{sub}</span></span>
  </button>
);

function MeetingHeader({ m }) {
  return (
    <div className="rounded-3xl bg-[#2D3B2D] text-[#FDFBF7] p-6 sm:p-8 grain">
      <div className="flex justify-between gap-3 items-start">
        <span className="text-xs font-mono opacity-80">{m.code}</span>
        <StatusBadge status={m.status} testid="public-meeting-status" />
      </div>
      <h1 className="font-serif text-2xl sm:text-3xl font-bold mt-3 leading-snug break-words [overflow-wrap:anywhere]" data-testid="public-meeting-title">{m.title}</h1>
      <div className="mt-4 grid gap-1.5 text-sm sm:text-base text-[#E5DEC9]">
        <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4" />{fmtDate(m.date)}</span>
        <span className="flex items-center gap-2"><Clock className="h-4 w-4" />{fmtClock(m.start_time)}{m.end_time ? ` – ${fmtClock(m.end_time)}` : ""}</span>
        <span className="flex items-center gap-2"><MapPin className="h-4 w-4" />{m.venue} · {m.format}</span>
      </div>
    </div>
  );
}

function ReviewList({ v, rep, dir }) {
  const gkk = dir.gkks.find((g) => g.id === v.gkk_id);
  const min = dir.ministries.find((x) => x.id === rep.ministry_id);
  const rows = v.type === "external"
    ? [["Name", [v.title, v.full_name].filter(Boolean).join(" ")], ["Organization", v.organization], ["Position", v.designation], ["Diocese / Congregation", v.diocese], ["Address", [v.barangay, v.city, v.province].filter(Boolean).join(", ")], ["Mobile", v.mobile], ["Role in meeting", rep.role_in_meeting]]
    : [["Name", [v.first_name, v.middle_name, v.last_name, v.suffix].filter(Boolean).join(" ")], ["Zone", dir.zones.find((z) => z.id === v.zone_id)?.name], ["GKK", gkk && `${gkk.name} – ${gkk.locality}`], ["Unit", dir.units.find((u) => u.id === v.unit_id)?.name], ["Ministry", min?.name], ["Position", rep.position === "Other" ? rep.position_other : rep.position], ["Address", [v.address, v.barangay, v.city].filter(Boolean).join(", ")], ["Mobile", v.mobile], ["Email", v.email]];
  return (
    <dl className="sfap-card divide-y divide-[#F0EADC]" data-testid="review-list">
      {rows.map(([k, val]) => (
        <div key={k} className="grid grid-cols-[130px_1fr] gap-3 px-5 py-3 text-base"><dt className="text-stone-500 text-sm">{k}</dt><dd className="font-semibold text-stone-900 break-words">{val || "—"}</dd></div>
      ))}
    </dl>
  );
}

export default function Register() {
  const { mid } = useParams();
  const [sp] = useSearchParams();
  const { t } = useLang();
  const [m, setM] = useState(null);
  const [dir, setDir] = useState(null);
  const [err, setErr] = useState(null);
  const [step, setStep] = useState("type");
  const [ptype, setPtype] = useState("parish");
  const [v, setV] = useState(emptyParticipant());
  const [rep, setRep] = useState({});
  const [pin, setPin] = useState("");
  const [consent, setConsent] = useState(false);
  const [comms, setComms] = useState(false);
  const [onsite, setOnsite] = useState(sp.get("c") || "");
  const [login, setLogin] = useState({ code: "", pin: "" });
  const [profile, setProfile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    Promise.all([pApi.get(`/meetings/${mid}`), api.get("/directory")])
      .then(([a, b]) => { setM(a.data); setDir(b.data); setSessionId(a.data.sessions?.[0]?.id); })
      .catch((e) => setErr(errMsg(e)));
  }, [mid]);

  const go = (s) => { setMsg(null); setStep(s); window.scrollTo({ top: 0 }); };
  if (err) return <Shell><div className="sfap-card p-8 text-center" data-testid="public-error"><AlertCircle className="mx-auto h-10 w-10 text-[#C85A32]" /><p className="mt-3 text-lg">{err}</p></div></Shell>;
  if (!m || !dir) return <Shell><Loading /></Shell>;

  const pre = !!m.preregistration_open;
  const canRegister = m.status === "open" || pre;
  const body = (extra = {}) => ({ session_id: sessionId, represent: rep, onsite_code: onsite || null, ...extra });

  const submitNew = async () => {
    setBusy(true);
    try {
      const r = await pApi.post(`/meetings/${m.id}/register`, body({ participant: { ...withAffiliation(v, rep), consent_comms: comms }, pin, consent }));
      sessionStorage.setItem(PART_KEY, r.data.token);
      setResult(r.data);
      go("done");
    } catch (e) { setMsg(errMsg(e)); } finally { setBusy(false); }
  };
  const doLogin = async () => {
    setBusy(true);
    try {
      const r = await pApi.post("/participant/login", login);
      sessionStorage.setItem(PART_KEY, r.data.token);
      setProfile(r.data.profile);
      setV({ ...emptyParticipant(r.data.profile.type), ...r.data.profile });
      const a = r.data.profile.affiliations?.[0];
      setRep(a ? { ministry_id: a.ministry_id, position: a.position } : {});
      go("confirm");
    } catch (e) { setMsg(errMsg(e)); } finally { setBusy(false); }
  };
  const submitReturning = async () => {
    setBusy(true);
    try {
      const r = await pApi.post(`/meetings/${m.id}/register`, body());
      setResult({ ...r.data, participant_code: profile.code });
      go("done");
    } catch (e) {
      if (e.response?.status === 409) {
        const r = await pApi.get(`/meetings/${m.id}/my-record`);
        const rec = r.data.records.find((x) => x.session_id === sessionId) || r.data.records[0];
        setResult({ record: rec, participant_code: profile.code, verified: ["present", "late"].includes(rec?.status), already: true, pre_registered: rec?.status === "pre_registered", name: profile.display_name });
        go("done");
      } else setMsg(errMsg(e));
    } finally { setBusy(false); }
  };
  const saveEdit = async () => {
    setBusy(true);
    try {
      const r = await pApi.put(`/meetings/${m.id}/my-record`, { participant: withAffiliation(v, rep), represent: rep, session_id: sessionId, onsite_code: onsite || null });
      setResult((x) => ({ ...x, record: r.data.record, verified: ["present", "late"].includes(r.data.record.status) }));
      setMsg(null);
      go("done");
      setMsg(t("saved"));
    } catch (e) { setMsg(errMsg(e)); } finally { setBusy(false); }
  };
  const startEdit = async () => {
    const r = await pApi.get(`/meetings/${m.id}/my-record`);
    const p = r.data.profile;
    setV({ ...emptyParticipant(p.type), ...p });
    const rec = r.data.records.find((x) => x.session_id === sessionId) || r.data.records[0];
    if (rec) setRep({ ministry_id: rec.snap.ministry_id, position: rec.snap.position, role_in_meeting: rec.snap.role_in_meeting, topic: rec.snap.topic });
    go("edit");
  };

  const repOk = v.type === "external" ? !!rep.role_in_meeting : !!rep.position;
  const Err = () => msg && <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-base" data-testid="form-message" role="alert">{msg}</div>;

  return (
    <Shell>
      <MeetingHeader m={m} />
      {!canRegister && (
        <div className="sfap-card p-6 text-center" data-testid="registration-closed-message">
          <Hourglass className="mx-auto h-9 w-9 text-[#D97706]" />
          <p className="font-serif text-xl font-bold mt-3">{t("notOpen")}</p>
          <p className="text-stone-600 mt-1">{m.status_message}</p>
        </div>
      )}
      {canRegister && m.sessions?.length > 1 && step !== "done" && (
        <Field label={t("session")}>
          <select className={inputCls} value={sessionId || ""} onChange={(e) => setSessionId(e.target.value)} data-testid="session-select">
            {m.sessions.map((s) => <option key={s.id} value={s.id}>{s.label} · {fmtDate(s.date)} {fmtClock(s.start_time)}</option>)}
          </select>
        </Field>
      )}
      {canRegister && step === "type" && (
        <div className="grid gap-4 fade-up">
          {pre && (
            <div className="rounded-2xl bg-violet-50 border border-violet-200 p-4 text-center" data-testid="prereg-open-banner">
              <p className="text-[#5C3A21] font-bold">{t("preReg")}</p>
              <p className="text-sm text-stone-600 mt-1">{m.status_message}{m.preregistration_deadline ? ` Deadline: ${new Date(m.preregistration_deadline).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}` : ""}</p>
              <p className="text-sm text-stone-600 mt-1">Already pre-registered? Choose “I have registered before” to confirm or cancel your attendance.</p>
            </div>
          )}
          <h2 className="font-serif text-xl sm:text-2xl font-bold text-[#2D3B2D] text-center mt-2">{t("how")}</h2>
          <Choice icon={HomeIcon} title={t("parish")} sub={t("parishSub")} testid="choose-parish" onClick={() => { setPtype("parish"); setV(emptyParticipant("parish")); go("mode"); }} />
          <Choice icon={Globe} title={t("guest")} sub={t("guestSub")} testid="choose-guest" onClick={() => { setPtype("external"); setV(emptyParticipant("external")); setRep({ role_in_meeting: "Ordinary Participant" }); go("mode"); }} />
        </div>
      )}
      {canRegister && step === "mode" && (
        <div className="grid gap-4 fade-up">
          <Choice icon={UserPlus} title={t("first")} sub={t("firstSub")} testid="choose-new" onClick={() => go("form")} />
          <Choice icon={KeyRound} title={t("returning")} sub={t("returningSub")} testid="choose-returning" onClick={() => go("login")} />
          <button className="btn-big btn-ghost" onClick={() => go("type")} data-testid="back-btn">{t("back")}</button>
        </div>
      )}
      {canRegister && step === "login" && (
        <div className="grid gap-4 fade-up">
          <Section title={t("verify")}>
            <div className="grid gap-4">
              <Field label={t("code")}><input className={`${inputCls} h-14 text-lg font-mono uppercase`} placeholder="SFAP-000123" value={login.code} onChange={(e) => setLogin({ ...login, code: e.target.value })} data-testid="returning-code" /></Field>
              <Field label={t("pinShort")}><input className={`${inputCls} h-14 text-2xl tracking-[0.5em] font-mono`} type="password" inputMode="numeric" maxLength={4} value={login.pin} onChange={(e) => setLogin({ ...login, pin: e.target.value.replace(/\D/g, "") })} data-testid="returning-pin" /></Field>
              <p className="text-sm text-stone-600">{t("forgot")}</p>
            </div>
          </Section>
          <Err />
          <button className="btn-big btn-primary" disabled={busy || !login.code || login.pin.length !== 4} onClick={doLogin} data-testid="returning-verify-btn">{t("verify")}</button>
          <button className="btn-big btn-ghost" onClick={() => go("mode")}>{t("back")}</button>
        </div>
      )}
      {canRegister && step === "confirm" && profile && (
        <div className="grid gap-4 fade-up">
          <div className="sfap-card p-6" data-testid="returning-profile-card">
            <p className="text-sm text-stone-500">{t("welcomeBack")} · {profile.code}</p>
            <p className="font-serif text-2xl font-bold mt-1">{profile.display_name}</p>
            <p className="text-stone-600 mt-1">{profile.type === "parish" ? dir.gkks.find((g) => g.id === profile.gkk_id)?.label : profile.organization}</p>
            <p className="mt-3 font-semibold text-[#2D3B2D]">{t("confirmIdentity")}</p>
          </div>
          <RepresentFields value={rep} onChange={setRep} dir={dir} t={t} type={profile.type} />
          {!pre && <OnsiteBox t={t} onsite={onsite} setOnsite={setOnsite} show={m.code_verification} />}
          <Err />
          <button className="btn-big btn-accent" disabled={busy || (!repOk && !pre)} onClick={submitReturning} data-testid="returning-submit-btn">{busy ? t("submitting") : `${t("yesMe")} — ${t("submit")}`}</button>
        </div>
      )}
      {canRegister && step === "form" && (
        <div className="grid gap-4 fade-up">
          <ParticipantFields value={v} onChange={setV} dir={dir} t={t} prefix="reg" />
          <RepresentFields value={rep} onChange={setRep} dir={dir} t={t} type={ptype} />
          <Section title={t("pin")}>
            <input className={`${inputCls} h-14 text-2xl tracking-[0.5em] font-mono max-w-[200px]`} type="password" inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid="reg-pin" />
            <p className="text-sm text-stone-600 mt-2">{t("pinHelp")}</p>
          </Section>
          <Section title={t("privacy")}>
            <p className="text-sm text-stone-700 leading-relaxed" data-testid="privacy-notice">{dir.privacy_notice}</p>
            <label className="flex items-start gap-3 mt-4 text-base min-h-[44px]"><input type="checkbox" className="h-6 w-6 mt-0.5 accent-[#2D3B2D]" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="reg-consent" />{t("agree")}</label>
            <label className="flex items-start gap-3 mt-2 text-sm text-stone-600 min-h-[44px]"><input type="checkbox" className="h-5 w-5 mt-0.5 accent-[#2D3B2D]" checked={comms} onChange={(e) => setComms(e.target.checked)} data-testid="reg-comms" />{t("comms")}</label>
          </Section>
          {!(validParticipant(v) && repOk && pin.length === 4 && consent) && <p className="text-sm text-stone-500 text-center">{t("required")}</p>}
          <button className="btn-big btn-primary" disabled={!(validParticipant(v) && repOk && pin.length === 4 && consent)} onClick={() => go("review")} data-testid="reg-continue-btn">{t("next")}</button>
          <button className="btn-big btn-ghost" onClick={() => go("mode")}>{t("back")}</button>
        </div>
      )}
      {canRegister && step === "review" && (
        <div className="grid gap-4 fade-up">
          <h2 className="font-serif text-xl font-bold text-[#2D3B2D]">{t("review")}</h2>
          <ReviewList v={v} rep={rep} dir={dir} />
          {!pre && <OnsiteBox t={t} onsite={onsite} setOnsite={setOnsite} show={m.code_verification} />}
          <Err />
          <button className="btn-big btn-accent" disabled={busy} onClick={submitNew} data-testid="reg-submit-btn">{busy ? t("submitting") : t("submit")}</button>
          <button className="btn-big btn-ghost" onClick={() => go("form")} data-testid="reg-back-to-form">{t("back")}</button>
        </div>
      )}
      {step === "edit" && (
        <div className="grid gap-4 fade-up">
          <ParticipantFields value={v} onChange={setV} dir={dir} t={t} prefix="edit" />
          <RepresentFields value={rep} onChange={setRep} dir={dir} t={t} type={v.type} prefix="edit-rep" />
          {result && !result.verified && <OnsiteBox t={t} onsite={onsite} setOnsite={setOnsite} show={m.code_verification} />}
          <Err />
          <button className="btn-big btn-primary" disabled={busy || !validParticipant(v)} onClick={saveEdit} data-testid="edit-save-btn">{t("save")}</button>
          <button className="btn-big btn-ghost" onClick={() => go("done")}>{t("back")}</button>
        </div>
      )}
      {step === "done" && result && (
        <div className="grid gap-4 fade-up" data-testid="registration-done">
          <div className={`rounded-3xl p-6 sm:p-8 text-center border ${result.verified ? "bg-green-50 border-green-200" : "bg-sky-50 border-sky-200"}`}>
            {result.verified ? <CheckCircle2 className="mx-auto h-14 w-14 text-green-700" /> : <Hourglass className="mx-auto h-14 w-14 text-sky-700" />}
            <p className="font-serif text-2xl font-bold mt-3">{result.pre_registered ? t("preRegDone") : t("done")}</p>
            <p className="mt-2 text-lg font-bold tracking-wide" data-testid="registration-result-status">{result.pre_registered ? "PRE-REGISTERED" : result.verified ? t("verifiedMsg") : t("pendingMsg")}</p>
            {result.record?.checkin_at && <p className="text-sm text-stone-600 mt-1">Check-in: {new Date(result.record.checkin_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</p>}
          </div>
          <div className="sfap-card p-6 text-center">
            <p className="text-sm uppercase tracking-widest text-stone-500">{t("yourId")}</p>
            <p className="font-mono text-3xl sm:text-4xl font-bold text-[#5C3A21] mt-2" data-testid="participant-code">{result.participant_code}</p>
            <p className="text-sm text-stone-600 mt-3 mb-5">{t("saveId")}</p>
            <IdCardBlock code={result.participant_code} name={result.name || profile?.display_name || [v.title, v.full_name, v.first_name, v.middle_name, v.last_name, v.suffix].filter(Boolean).join(" ")}
              line={v.type === "external" ? v.organization : dir.gkks.find((g) => g.id === v.gkk_id)?.label} type={v.type} />
          </div>
          {msg && <p className="text-center text-green-800 font-semibold" data-testid="form-message">{msg}</p>}
          {result.pre_registered && <RsvpBox m={m} result={result} setResult={setResult} sessionId={sessionId} />}
          {m.status === "open" && <button className="btn-big btn-ghost" onClick={startEdit} data-testid="edit-my-entry-btn"><Pencil className="h-5 w-5" />{t("edit")}</button>}
          <Link to="/me" className="text-center text-sm text-stone-600 underline">Participant Profile</Link>
        </div>
      )}
    </Shell>
  );
}

function RsvpBox({ m, result, setResult, sessionId }) {
  const [busy, setBusy] = useState(false);
  const rec = result.record || {};
  const act = async (action) => {
    if (action === "cancel" && !window.confirm("Cancel your pre-registration? Your reserved seat will be given to the next person on the waitlist.")) return;
    setBusy(true);
    try {
      const r = await pApi.post(`/meetings/${m.id}/rsvp`, { action, session_id: sessionId });
      setResult({ ...result, record: r.data.record });
    } catch (e) { window.alert(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="sfap-card p-5 grid gap-3" data-testid="rsvp-box">
      {rec.waitlisted && <p className="rounded-xl bg-amber-50 border border-amber-300 p-3 text-amber-900 font-semibold" data-testid="rsvp-waitlisted">The venue is full. You are on the WAITLIST and will get a seat automatically if someone cancels. Check this page again before the meeting.</p>}
      <p className="font-semibold" data-testid="rsvp-status">{rec.rsvp === "confirmed" ? "You have CONFIRMED that you will attend. Thank you!" : rec.rsvp === "cancelled" ? "Your pre-registration is CANCELLED." : "Please confirm your attendance closer to the meeting so we can prepare the right number of chairs and materials."}</p>
      {rec.rsvp !== "cancelled" ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {rec.rsvp !== "confirmed" && <button className="btn-big btn-primary" disabled={busy} onClick={() => act("confirm")} data-testid="rsvp-confirm-btn">Yes, I will attend</button>}
          <button className="btn-big btn-ghost" disabled={busy} onClick={() => act("cancel")} data-testid="rsvp-cancel-btn">I can no longer attend</button>
        </div>
      ) : <p className="text-sm text-stone-600">Changed your mind? Start again and pre-register while pre-registration is open.</p>}
    </div>
  );
}

function OnsiteBox({ t, onsite, setOnsite, show }) {
  if (!show) return null;
  return (
    <Section title={t("onsite")}>
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-[#2D3B2D] shrink-0" />
        <input className={`${inputCls} h-14 text-2xl tracking-[0.4em] font-mono max-w-[220px]`} inputMode="numeric" maxLength={6} value={onsite} onChange={(e) => setOnsite(e.target.value.replace(/\D/g, ""))} data-testid="onsite-code-input" />
      </div>
      <p className="text-sm text-stone-600 mt-2">{t("onsiteHelp")}</p>
    </Section>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[17px]">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-8 py-4 border-b border-[#E5DEC9]">
        <Link to="/"><ParishMark small /></Link>
        <LangToggle />
      </header>
      <main className="mx-auto max-w-2xl px-4 py-6 sm:py-10 grid gap-5">{children}</main>
    </div>
  );
}
